import { useEffect, useState } from "react"
import { PropsSingle } from "react-day-picker"
import { useTranslation } from "react-i18next"
import { addWeeks, endOfYear, parse } from "date-fns"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { DateInput, VISUAL_DATE_FORMAT, VISUAL_DATE_SEPARATOR } from "@/components/ui/date-picker"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { formatDate } from "@/lib/format-date"
import { validDateRegex, validDateWhileTypingRegex } from "@/modules/patients/libs/date-range-regex"

type DateField = "dateFrom" | "dateTo"

const TEST_ID: Record<DateField, string> = {
  dateFrom: "start-date-picker",
  dateTo: "end-date-picker",
}

type Props = {
  field: DateField
  value: Date | undefined
  onSelect?: (date: Date) => void
  error?: string
  label: string
  disabledMatcher?: (date: Date) => boolean
  hideActionButtons?: boolean
  /** Start date the quick-preset buttons ("8 weeks", "Until end of year") are computed from. */
  rangeStartDate?: Date
  ref?: React.Ref<HTMLInputElement>
  disabled?: boolean
}

export const TimeWindowDatePicker = (props: Props) => {
  const { field, value, hideActionButtons, onSelect, error, label, disabledMatcher, rangeStartDate, ref, disabled } =
    props

  const isStart = field === "dateFrom"
  const showActionButtons = !isStart && !hideActionButtons

  const [open, setOpen] = useState(false)
  const [fieldValue, setFieldValue] = useState<string | undefined>(formatDate(value, VISUAL_DATE_FORMAT))
  const { t } = useTranslation()

  // The form value can hold an invalid Date (NaN) to mark a cleared/unparseable input — guard against
  // it so we never overwrite what the user is typing with an "Invalid Date" string.
  const validValue = value && !Number.isNaN(value.getTime()) ? value : undefined

  const validateDateFormat = (input: string) => validDateRegex.test(input)

  useEffect(() => {
    if (value && !Number.isNaN(value.getTime())) setFieldValue(formatDate(value, VISUAL_DATE_FORMAT))
  }, [value])

  const handleSelect: PropsSingle["onSelect"] = (date, selectedDay) => {
    if (formatDate(value) === formatDate(selectedDay)) return

    onSelect?.(date ?? new Date(NaN))
    setFieldValue(date ? formatDate(date, VISUAL_DATE_FORMAT) : "")

    handleOpenChange(false)
  }

  const handleOpenChange = (state: boolean) => {
    setOpen(state)
  }

  const keyPressFunc = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      const val = fieldValue
      if (val && (val.length === 3 || val.length === 6)) {
        setFieldValue(val.slice(0, val.length - 1))
      }
    }
  }

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    let val = evt.target.value

    if (val && !validDateWhileTypingRegex.test(val)) {
      evt.preventDefault()
      return
    }

    if (val.length === 2 || val.length === 5) {
      val += VISUAL_DATE_SEPARATOR
    }

    setFieldValue(val)
    validateDate(val)
  }

  const handleInputFieldClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    if (disabled) return
    setOpen(true)
  }

  const handleCalendarIconClick = (e: React.SyntheticEvent<HTMLElement, Event>) => {
    e.preventDefault()
    setOpen((open) => !open)
  }

  const validateDate = (input: string) => {
    // parse returns an Invalid Date (a Date whose time value is NaN) when the input cannot be parsed.
    // See https://date-fns.org/v3.1.0/docs/parse
    const parsedDate = parse(input, VISUAL_DATE_FORMAT, new Date())
    const isValidDate = validateDateFormat(input) && !Number.isNaN(parsedDate.getTime())

    // Always push the value into the form: a valid Date when the input parses, otherwise an invalid
    // Date so the Zod resolver (not a fragile manual error that the next resolver pass would wipe)
    // flags the field. This keeps Save disabled while the date is empty/invalid (bug 48823).
    onSelect?.(isValidDate && !disabledMatcher?.(parsedDate) ? parsedDate : new Date(NaN))
  }

  const applyPreset = (computeFrom: (startDate: Date) => Date) => {
    if (!(rangeStartDate instanceof Date) || isNaN(rangeStartDate.getTime())) {
      return
    }

    const presetDate = computeFrom(rangeStartDate)
    onSelect?.(presetDate)
    setFieldValue(formatDate(presetDate, VISUAL_DATE_FORMAT))
    handleOpenChange(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild onClick={handleInputFieldClick}>
        <DateInput
          ref={ref}
          data-testid={TEST_ID[field]}
          label={label}
          type="text"
          focus={open}
          error={error}
          translateError
          readOnly={disabled}
          disabled={disabled}
          onChange={handleChange}
          onKeyDown={keyPressFunc}
          onCalendarIconClick={disabled ? undefined : handleCalendarIconClick}
          value={fieldValue}
          onBlur={isStart ? (event) => validateDate(event.target.value) : undefined}
        />
      </PopoverTrigger>

      <PopoverContent
        sideOffset={0}
        className="bg-surface w-auto rounded-lg p-0"
        align="start"
        onOpenAutoFocus={(e: Event) => e.preventDefault()}
        onCloseAutoFocus={(e: Event) => e.preventDefault()}>
        {showActionButtons && (
          <div className="bg-surface">
            <div className="m-2 flex flex-row flex-wrap gap-2 px-2 pt-4 pb-2">
              <Button
                className="inline-flex h-8 items-center rounded-md text-sm outline-0"
                variant="secondary"
                onClick={() => applyPreset((startDate) => addWeeks(startDate, 8))}>
                {t("patient.agenda.timeWindowForm.calendarEightWeeks")}
              </Button>
              <Button
                className="inline-flex h-8 items-center rounded-md text-sm outline-0"
                variant="secondary"
                onClick={() => applyPreset((startDate) => endOfYear(startDate))}>
                {t("patient.agenda.timeWindowForm.calendarUntilEndOfYear")}
              </Button>
            </div>
          </div>
        )}
        <Calendar
          disabledMatcher={disabledMatcher}
          fixedWeeks
          onSelect={handleSelect}
          selected={validValue}
          today={validValue}
          className="pb-2"
          mode="single"
        />
      </PopoverContent>
    </Popover>
  )
}
